<template>
  <div class="email-body-editor">
    <div class="email-body-editor__actions d-flex align-center justify-space-between mb-2">
      <v-menu location="bottom end">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            size="small"
            color="customizer-primary"
            variant="tonal"
            prepend-icon="mdi-tag-plus-outline"
          >
            Insert Token
          </v-btn>
        </template>

        <v-list bg-color="customizer-surface" density="compact" min-width="190">
          <template v-for="group in mergeTagGroups" :key="group.category">
            <v-list-subheader>{{ group.category }}</v-list-subheader>
            <v-list-item
              v-for="tag in group.tags"
              :key="tag.token"
              :title="tag.label"
              :subtitle="tag.token"
              @click="insertMergeTag(tag)"
            >
              <template #prepend>
                <v-icon size="small">mdi-code-brackets</v-icon>
              </template>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>

      <span class="text-caption text-medium-emphasis">HTML body</span>
    </div>

    <div id="email-quill-toolbar">
    
      <span class="ql-formats">
        <button class="ql-bold" type="button" title="Bold" aria-label="Bold"></button>
        <button class="ql-italic" type="button" title="Italic" aria-label="Italic"></button>
        <button class="ql-underline" type="button" title="Underline" aria-label="Underline"></button>
      </span>
      <span class="ql-formats">
        <button class="ql-list" type="button" value="ordered" title="Numbered list" aria-label="Numbered list"></button>
      </span>
      <span class="ql-formats">
        <button class="ql-align" type="button" value="" title="Align left" aria-label="Align left"></button>
        <button class="ql-align" type="button" value="center" title="Align center" aria-label="Align center"></button>
        <button class="ql-align" type="button" value="right" title="Align right" aria-label="Align right"></button>
        <button class="ql-align" type="button" value="justify" title="Justify" aria-label="Justify"></button>
      </span>
      <span class="ql-formats">
        <button class="ql-link" type="button" title="Link" aria-label="Link"></button>
        <button class="ql-clean" type="button" title="Clear formatting" aria-label="Clear formatting"></button>
      </span>
    </div>

    <QuillEditor
      :key="editorKey"
      ref="editorRef"
      v-model:content="content"
      content-type="html"
      theme="snow"
      :toolbar="toolbar"
      class="email-quill"
      @ready="onReady"
      @selection-change="onSelectionChange"
    />
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  mergeTagGroups: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue'])

const editorRef = ref(null)
const quill = ref(null)
const content = ref(normalizeMergeTagsHtml(props.modelValue))
const lastRange = ref(null)
const editorKey = ref(0)

const toolbar = '#email-quill-toolbar'

watch(
  () => props.modelValue,
  (value) => {
    const normalizedValue = normalizeMergeTagsHtml(value)
    if (normalizedValue !== content.value) {
      content.value = normalizedValue
    }
  }
)

watch(content, (value) => {
  emit('update:modelValue', normalizeMergeTagsHtml(value))
})

function normalizeMergeTagsHtml(value) {
  if (value === null || value === undefined) return ''

  const rawHtml = String(value).trim()
  const textValue = rawHtml
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
    .toLowerCase()

  if (textValue === 'null') return ''

  const html = String(value || '').replace(/[\uFEFF\u200B]/g, '')

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html.replace(/<span\b[^>]*data-token=["']?([^"' >]+)["']?[^>]*>.*?<\/span>/gi, '$1')
  }

  const parser = new DOMParser()
  const documentFragment = parser.parseFromString(`<div>${html}</div>`, 'text/html')
  const wrapper = documentFragment.body.firstElementChild

  if (!wrapper) return html

  wrapper
    .querySelectorAll('.email-merge-tag, [data-token], [contenteditable="false"]')
    .forEach((node) => {
      const token = node.getAttribute('data-token') || node.textContent || ''
      node.replaceWith(documentFragment.createTextNode(token.replace(/[\uFEFF\u200B]/g, '')))
    })

  normalizeQuillListsForEmail(wrapper, documentFragment)
  inlineQuillAlignmentForEmail(wrapper)

  return wrapper.innerHTML.replace(/[\uFEFF\u200B]/g, '')
}

function normalizeQuillListsForEmail(wrapper, documentFragment) {
  wrapper.querySelectorAll('ol, ul').forEach((list) => {
    const listItems = Array.from(list.children).filter((child) => child.tagName === 'LI')
    if (!listItems.length) return

    const normalizedLists = []
    let currentList = null
    let currentListType = ''

    listItems.forEach((item) => {
      const listType = item.dataset.list === 'bullet' ? 'ul' : 'ol'

      if (listType !== currentListType) {
        currentListType = listType
        currentList = createEmailList(list, listType, documentFragment)
        normalizedLists.push(currentList)
      }

      cleanQuillListItem(item)
      currentList.appendChild(item)
    })

    list.replaceWith(...normalizedLists)
  })
}

function createEmailList(sourceList, listType, documentFragment) {
  const normalizedList = documentFragment.createElement(listType)

  Array.from(sourceList.attributes).forEach((attribute) => {
    if (attribute.name !== 'data-list') {
      normalizedList.setAttribute(attribute.name, attribute.value)
    }
  })

  return normalizedList
}

function cleanQuillListItem(item) {
  const indentClass = Array.from(item.classList).find((className) => className.startsWith('ql-indent-'))
  const indentLevel = Number(indentClass?.replace('ql-indent-', '') || 0)

  item.removeAttribute('data-list')
  item.querySelectorAll('.ql-ui').forEach((node) => node.remove())

  if (indentLevel > 0) {
    item.style.marginLeft = `${indentLevel * 1.5}em`
    item.classList.remove(indentClass)
  }

  if (!item.getAttribute('class')) {
    item.removeAttribute('class')
  }
}

function inlineQuillAlignmentForEmail(wrapper) {
  wrapper.querySelectorAll('[class*="ql-align-"]').forEach((node) => {
    const alignClass = Array.from(node.classList).find((className) => className.startsWith('ql-align-'))
    const alignment = alignClass?.replace('ql-align-', '')

    if (alignment) {
      node.style.textAlign = alignment
      node.classList.remove(alignClass)
    }

    if (!node.getAttribute('class')) {
      node.removeAttribute('class')
    }
  })
}

function onReady(editor) {
  quill.value = editor
  registerHistoryToolbarHandlers(editor)
  resetEditorToCleanHtml(editor)
}

function registerHistoryToolbarHandlers(editor) {
  const editorToolbar = editor.getModule('toolbar')

  if (!editorToolbar) return

  editorToolbar.addHandler('list', (value) => {
    const range = editor.getSelection(true)
    const currentFormats = editor.getFormat(range)
    const nextListType = value && currentFormats.list !== value ? value : false

    editor.format('list', nextListType, 'user')
  })
}

function resetEditorToCleanHtml(editor) {
  const cleanedHtml = normalizeMergeTagsHtml(editor.root.innerHTML || content.value)

  if (cleanedHtml === editor.root.innerHTML && cleanedHtml === content.value) return

  content.value = cleanedHtml
  emit('update:modelValue', cleanedHtml)

  try {
    editor.clipboard.dangerouslyPasteHTML(cleanedHtml, 'silent')
  } catch {
    editor.root.innerHTML = cleanedHtml
    editor.update('silent')
  }
}

function onSelectionChange({ range } = {}) {
  if (range) {
    lastRange.value = range
  }
}

function getEditor() {
  return quill.value || editorRef.value?.getQuill?.() || null
}

function getSafeInsertRange(editor) {
  const fallbackIndex = Math.max(editor.getLength() - 1, 0)
  let range = lastRange.value || { index: fallbackIndex, length: 0 }

  const maxIndex = Math.max(editor.getLength() - 1, 0)
  return {
    index: Math.min(Math.max(range.index || 0, 0), maxIndex),
    length: Math.max(range.length || 0, 0),
  }
}

function insertMergeTag(tag) {
  const editor = getEditor()
  if (!editor || !tag?.token) return

  const range = getSafeInsertRange(editor)
  const tokenText = `${tag.token} `

  if (range.length) {
    editor.editor.deleteText(range.index, range.length)
  }

  editor.editor.insertText(range.index, tokenText)
  editor.update('silent')
  lastRange.value = { index: range.index + tokenText.length, length: 0 }

  content.value = normalizeMergeTagsHtml(editor.root.innerHTML)
}
</script>

<style scoped>
.email-body-editor {
  width: 100%;
}

.email-quill {
  background: rgb(var(--v-theme-customizer-surface));
  border-radius: 8px;
}

#email-quill-toolbar {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

#email-quill-toolbar .email-quill-history-button {
  font-size: 17px;
  line-height: 1;
}

#email-quill-toolbar .email-quill-history-button:hover {
  color: rgb(var(--v-theme-customizer-info));
}

.email-quill :deep(.ql-container) {
  min-height: 220px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  font-size: 0.95rem;
}

.email-quill :deep(.ql-editor) {
  min-height: 220px;
}

</style>